/**
 * FreeAssessmentTerminal — full-screen CLI/DOS examiner experience.
 * Stages:
 *   WELCOME    — full-screen hero with typed greeting + Sandra's TTS voice
 *   MIC_CHECK  — beautiful modal: tests speaker, then mic
 *   BOOT       — terminal boot sequence
 *   COLLECT    — name (CLI) → email (modal) → country (chips) → phone (modal) → exam (chips)
 *   CREATE_ACCOUNT
 *   INSTRUCTIONS
 *   ASSESSMENT
 *   RESULT
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Mic, ChevronRight, Check, Lock, Play, Pause, Loader2, Sparkles, Volume2,
  X, Mail, Phone as PhoneIcon, AlertCircle, Headphones, Search, Wifi, User, Clock, Radio,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const COUNTRY_CODES = [
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "IN", dial: "+91", flag: "🇮🇳", name: "India" },
  { code: "US", dial: "+1",  flag: "🇺🇸", name: "United States" },
  { code: "AE", dial: "+971", flag: "🇦🇪", name: "United Arab Emirates" },
  { code: "AU", dial: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "CA", dial: "+1",  flag: "🇨🇦", name: "Canada" },
  { code: "PK", dial: "+92", flag: "🇵🇰", name: "Pakistan" },
  { code: "BD", dial: "+880", flag: "🇧🇩", name: "Bangladesh" },
  { code: "NG", dial: "+234", flag: "🇳🇬", name: "Nigeria" },
  { code: "PH", dial: "+63", flag: "🇵🇭", name: "Philippines" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "France" },
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Italy" },
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "Spain" },
  { code: "NL", dial: "+31", flag: "🇳🇱", name: "Netherlands" },
  { code: "BE", dial: "+32", flag: "🇧🇪", name: "Belgium" },
  { code: "CH", dial: "+41", flag: "🇨🇭", name: "Switzerland" },
  { code: "SE", dial: "+46", flag: "🇸🇪", name: "Sweden" },
  { code: "NO", dial: "+47", flag: "🇳🇴", name: "Norway" },
  { code: "DK", dial: "+45", flag: "🇩🇰", name: "Denmark" },
  { code: "FI", dial: "+358", flag: "🇫🇮", name: "Finland" },
  { code: "IE", dial: "+353", flag: "🇮🇪", name: "Ireland" },
  { code: "PL", dial: "+48", flag: "🇵🇱", name: "Poland" },
  { code: "PT", dial: "+351", flag: "🇵🇹", name: "Portugal" },
  { code: "GR", dial: "+30", flag: "🇬🇷", name: "Greece" },
  { code: "TR", dial: "+90", flag: "🇹🇷", name: "Turkey" },
  { code: "RU", dial: "+7", flag: "🇷🇺", name: "Russia" },
  { code: "UA", dial: "+380", flag: "🇺🇦", name: "Ukraine" },
  { code: "CN", dial: "+86", flag: "🇨🇳", name: "China" },
  { code: "JP", dial: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "KR", dial: "+82", flag: "🇰🇷", name: "South Korea" },
  { code: "SG", dial: "+65", flag: "🇸🇬", name: "Singapore" },
  { code: "MY", dial: "+60", flag: "🇲🇾", name: "Malaysia" },
  { code: "ID", dial: "+62", flag: "🇮🇩", name: "Indonesia" },
  { code: "TH", dial: "+66", flag: "🇹🇭", name: "Thailand" },
  { code: "VN", dial: "+84", flag: "🇻🇳", name: "Vietnam" },
  { code: "HK", dial: "+852", flag: "🇭🇰", name: "Hong Kong" },
  { code: "TW", dial: "+886", flag: "🇹🇼", name: "Taiwan" },
  { code: "NZ", dial: "+64", flag: "🇳🇿", name: "New Zealand" },
  { code: "ZA", dial: "+27", flag: "🇿🇦", name: "South Africa" },
  { code: "EG", dial: "+20", flag: "🇪🇬", name: "Egypt" },
  { code: "MA", dial: "+212", flag: "🇲🇦", name: "Morocco" },
  { code: "DZ", dial: "+213", flag: "🇩🇿", name: "Algeria" },
  { code: "KE", dial: "+254", flag: "🇰🇪", name: "Kenya" },
  { code: "GH", dial: "+233", flag: "🇬🇭", name: "Ghana" },
  { code: "ET", dial: "+251", flag: "🇪🇹", name: "Ethiopia" },
  { code: "UG", dial: "+256", flag: "🇺🇬", name: "Uganda" },
  { code: "TZ", dial: "+255", flag: "🇹🇿", name: "Tanzania" },
  { code: "SA", dial: "+966", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "QA", dial: "+974", flag: "🇶🇦", name: "Qatar" },
  { code: "KW", dial: "+965", flag: "🇰🇼", name: "Kuwait" },
  { code: "OM", dial: "+968", flag: "🇴🇲", name: "Oman" },
  { code: "BH", dial: "+973", flag: "🇧🇭", name: "Bahrain" },
  { code: "JO", dial: "+962", flag: "🇯🇴", name: "Jordan" },
  { code: "LB", dial: "+961", flag: "🇱🇧", name: "Lebanon" },
  { code: "IL", dial: "+972", flag: "🇮🇱", name: "Israel" },
  { code: "IR", dial: "+98", flag: "🇮🇷", name: "Iran" },
  { code: "IQ", dial: "+964", flag: "🇮🇶", name: "Iraq" },
  { code: "AF", dial: "+93", flag: "🇦🇫", name: "Afghanistan" },
  { code: "LK", dial: "+94", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "NP", dial: "+977", flag: "🇳🇵", name: "Nepal" },
  { code: "MX", dial: "+52", flag: "🇲🇽", name: "Mexico" },
  { code: "BR", dial: "+55", flag: "🇧🇷", name: "Brazil" },
  { code: "AR", dial: "+54", flag: "🇦🇷", name: "Argentina" },
  { code: "CL", dial: "+56", flag: "🇨🇱", name: "Chile" },
  { code: "CO", dial: "+57", flag: "🇨🇴", name: "Colombia" },
  { code: "PE", dial: "+51", flag: "🇵🇪", name: "Peru" },
  { code: "VE", dial: "+58", flag: "🇻🇪", name: "Venezuela" },
];

const EXAM_OPTIONS = [
  { id: "IELTS", label: "IELTS" },
  { id: "PTE",   label: "PTE Academic" },
  { id: "ELLT",  label: "Oxford ELLT" },
  { id: "DUO",   label: "Duolingo English Test" },
];

const QUESTIONS = [
  { n: 1, type: "warmup", text: "Tell me about your hometown — where it is, what it's known for, and one thing you love about it.", seconds: 60 },
  { n: 2, type: "cue-card", text: "Describe a skill you would like to learn in the next year. You should say what the skill is, why you want to learn it, how you plan to learn it, and how it will help your future.", seconds: 60 },
  { n: 3, type: "opinion", text: "Some people believe that online learning is more effective than classroom learning. What is your opinion and why?", seconds: 60 },
];

const T = {
  bg: "#0B0C0A",
  panel: "#0F1110",
  text: "#FCFAF3",
  textDim: "rgba(252,250,243,0.55)",
  primary: "#F77F00",
  primaryGlow: "rgba(247,127,0,0.65)",
  success: "#16A34A",
  danger: "#D62828",
  border: "rgba(247,127,0,0.25)",
};

// =================================================================
// Helpers
// =================================================================
function useTypewriter(lines, speed = 15, onDone) {
  const [rendered, setRendered] = useState([]);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    let stop = false;
    setRendered([]); setTyping(true);
    (async () => {
      const out = [];
      for (const line of lines) {
        const acc = { ...line, text: "" };
        out.push(acc);
        for (let i = 0; i < line.text.length; i++) {
          if (stop) return;
          acc.text += line.text[i];
          setRendered([...out]);
          const ch = line.text[i];
          await new Promise((r) => setTimeout(r, /[.!?]/.test(ch) ? speed * 6 : /[,;:]/.test(ch) ? speed * 3 : speed));
        }
        await new Promise((r) => setTimeout(r, 140));
      }
      setTyping(false);
      if (onDone) onDone();
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);
  return { rendered, typing };
}

async function speakSandra(text) {
  try {
    const res = await axios.post(`${API}/assessment/speak`, { text });
    const b64 = res.data?.audio_b64;
    if (!b64) return null;
    const audio = new Audio(`data:audio/mp3;base64,${b64}`);
    await audio.play();
    return new Promise((resolve) => { audio.onended = () => resolve(audio); setTimeout(() => resolve(audio), 18000); });
  } catch (e) { throw e; }
}

function Cursor({ big = false }) {
  return (
    <span className="inline-block align-middle ml-1" style={{
      width: big ? 18 : 14, height: big ? 28 : 22, background: T.primary, boxShadow: `0 0 14px ${T.primaryGlow}`,
      animation: "ttyBlink 1s steps(2) infinite",
    }} />
  );
}

function HudCorners({ size = 6 }) {
  return (
    <>
      <span className="absolute top-2 left-2 border-t-2 border-l-2" style={{ borderColor: T.primary, width: size * 4, height: size * 4 }} />
      <span className="absolute top-2 right-2 border-t-2 border-r-2" style={{ borderColor: T.primary, width: size * 4, height: size * 4 }} />
      <span className="absolute bottom-2 left-2 border-b-2 border-l-2" style={{ borderColor: T.primary, width: size * 4, height: size * 4 }} />
      <span className="absolute bottom-2 right-2 border-b-2 border-r-2" style={{ borderColor: T.primary, width: size * 4, height: size * 4 }} />
    </>
  );
}

function ScanLines() {
  return (
    <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 1px, transparent 1px, transparent 3px)"
    }} />
  );
}

function Line({ line, isLast }) {
  const isAI = line.who === "AI";
  const isSYS = line.who === "SYS";
  const ref = useRef(null);
  useEffect(() => {
    if (isLast && ref.current) {
      // Auto-scroll the closest scrollable ancestor to keep the latest text in view.
      ref.current.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [line.text, isLast]);
  return (
    <div ref={ref} className="flex items-start gap-3 mb-3">
      <span className="font-mono text-base lg:text-lg tracking-[0.15em] mt-1.5 select-none flex-shrink-0"
        style={{ color: isSYS ? T.textDim : T.primary, opacity: isAI ? 0.95 : 0.6 }}>
        {isSYS ? "//" : isAI ? "AI>" : "$_"}
      </span>
      <span className="font-mono text-xl lg:text-2xl leading-relaxed whitespace-pre-wrap"
        style={{ color: isSYS ? T.textDim : T.text, fontFamily: "'JetBrains Mono', 'Fira Mono', Consolas, monospace" }}>
        {line.text}
        {isLast && line.live && <Cursor />}
      </span>
    </div>
  );
}

// =================================================================
// Main component
// =================================================================
export default function FreeAssessmentTerminal() {
  const nav = useNavigate();
  const [stage, setStage] = useState("WELCOME");
  const [log, setLog] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [log, stage]);

  const push = (entries) => setLog((l) => [...l, ...entries]);

  return (
    <div className="w-full min-h-screen" style={{ background: T.bg, color: T.text }} data-testid="assessment-terminal-root">
      <style>{`
        @keyframes ttyBlink { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
        @keyframes ttyGlow { 0%,100% { box-shadow: 0 0 24px ${T.primaryGlow}, inset 0 0 12px rgba(247,127,0,0.18) } 50% { box-shadow: 0 0 36px ${T.primaryGlow}, inset 0 0 16px rgba(247,127,0,0.28) } }
        @keyframes ttyPulseRing { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(1.8);opacity:0} }
        @keyframes ttyHeroFade { 0%{opacity:0;transform:translateY(10px)} 100%{opacity:1;transform:translateY(0)} }
      `}</style>

      {stage === "WELCOME" && <WelcomeStage onContinue={() => setStage("MIC_CHECK")} />}

      {stage === "MIC_CHECK" && (
        <MicCheckModal
          onCancel={() => setStage("WELCOME")}
          onConfirm={() => setStage("BOOT")}
        />
      )}

      {Array.isArray(stage) && stage[0] === "CONNECTING" && (
        <ConnectingStage ctx={stage[1]} onDone={() => setStage(["EXAM_ROOM", stage[1]])} />
      )}
      {Array.isArray(stage) && stage[0] === "EXAM_ROOM" && (
        <ExamRoomStage ctx={stage[1]} nav={nav} />
      )}

      {!["WELCOME", "MIC_CHECK"].includes(stage) &&
        !(Array.isArray(stage) && (stage[0] === "CONNECTING" || stage[0] === "EXAM_ROOM")) && (
        <TerminalShell scrollRef={scrollRef} log={log}>
          {stage === "BOOT" && <BootStage onDone={(lines) => { push(lines); setStage("COLLECT"); }} />}
          {stage === "COLLECT" && (
            <CollectStage
              push={push}
              onDone={(formData) => setStage(["CREATE_ACCOUNT", formData])}
              onAlreadyAssessed={(email, existing, hint) => {
                // Email already has an assessment_results doc (STARTED, COMPLETED, or TERMINATED).
                // Route to the RESULT stage with a synthetic ctx so we never re-let them retry.
                const status = (existing?.status || "TERMINATED").toUpperCase();
                const overall = Number(existing?.overall_band ?? 0);
                const crit = {
                  fluency: Number(existing?.fluency ?? 0),
                  lexical: Number(existing?.lexical ?? 0),
                  grammar: Number(existing?.grammar ?? 0),
                  pronunciation: Number(existing?.pronunciation ?? 0),
                };
                const synth = {
                  email,
                  target_exam: existing?.target_exam || "IELTS",
                  full_name: email.split("@")[0],
                  terminated: status !== "COMPLETED",
                  status,
                  result: {
                    overall_band: overall,
                    criteria: crit,
                    summary: existing?.summary || hint
                      || "Your previous assessment was terminated before completion. Free assessments are limited to one attempt — please subscribe to access full mock tests.",
                    voice_summary: existing?.voice_summary || (
                      status === "COMPLETED"
                        ? null
                        : "Your previous assessment was ended before you finished, so I cannot grade you fairly. Free attempts are limited to one — join our hybrid programme and we will get you to your target band together. Join now."
                    ),
                    items: existing?.items || [],
                  },
                };
                push([
                  { who: "SYS", text: `// EMAIL ${email} already used — loading previous result…` },
                ]);
                setStage(["RESULT", synth]);
              }}
            />
          )}
          {Array.isArray(stage) && stage[0] === "CREATE_ACCOUNT" && (
            <CreateAccountStage push={push} form={stage[1]}
              onDone={(account) => setStage(["INSTRUCTIONS", { ...stage[1], account }])} />
          )}
          {Array.isArray(stage) && stage[0] === "INSTRUCTIONS" && (
            <InstructionsStage push={push} onStart={() => setStage(["CONNECTING", stage[1]])} />
          )}
          {Array.isArray(stage) && stage[0] === "RESULT" && (
            <ResultStage ctx={stage[1]} onSubscribe={() => nav("/pricing")} onDashboard={() => nav("/app")} />
          )}
        </TerminalShell>
      )}
    </div>
  );
}

// =================================================================
// Full-screen terminal shell (used after Welcome + Mic-check)
// =================================================================
function TerminalShell({ scrollRef, log, children }) {
  return (
    <div className="relative w-full min-h-screen flex flex-col" style={{ background: T.bg }}>
      {/* Top status bar (replaces the old separate header) */}
      <div className="flex items-center gap-3 px-6 py-3 border-b" style={{ borderColor: T.border }}>
        <div className="w-9 h-9 rounded-sm flex items-center justify-center"
          style={{ background: T.primary, color: "#000", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
          <span className="font-black text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>E</span>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>// englishtest.uk</div>
          <div className="font-bold text-base" style={{ fontFamily: "'Rajdhani', sans-serif" }}>EXAMINER · TERMINAL v2.0</div>
        </div>
        <div className="ml-auto flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textDim }}>
          <span className="w-2 h-2 rounded-full" style={{ background: T.success, boxShadow: `0 0 8px ${T.success}` }} />
          LIVE · AES-256
        </div>
      </div>

      {/* macOS dots row */}
      <div className="flex items-center gap-2 px-5 py-2.5 border-b" style={{ borderColor: T.border }}>
        <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
        <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
        <span className="w-3 h-3 rounded-full bg-[#28C840]" />
        <div className="flex-1 text-center font-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: T.textDim }}>
          examiner@englishtest.uk:~/assessment $
        </div>
      </div>

      {/* Scrollable terminal body */}
      <div ref={scrollRef} className="relative flex-1 overflow-y-auto px-6 lg:px-12 py-6"
        data-testid="assessment-terminal-log" style={{ minHeight: 0 }}>
        <ScanLines />
        <div className="relative">
          {log.map((line, i) => (
            <Line key={i} line={line} isLast={i === log.length - 1 && line.live} />
          ))}
          {children}
        </div>
      </div>

      <div className="px-6 py-3 border-t font-mono text-[10px] tracking-[0.3em] uppercase text-center" style={{ borderColor: T.border, color: T.textDim }}>
        // ENGLISHTEST.UK · CLOUD LAB TECHNOLOGIES · 256-BIT ENCRYPTED SESSION
      </div>
    </div>
  );
}

// =================================================================
// Stage 0 — WELCOME (full-screen hero + voice)
// =================================================================
function WelcomeStage({ onContinue }) {
  const [step, setStep] = useState(0);
  const heroLines = useMemo(() => ([
    "Welcome to EnglishTest.UK",
    "I'm Sandra — your AI examiner.",
  ]), []);
  const [typed, setTyped] = useState(["", ""]);
  const spoke = useRef(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const cachedB64 = useRef(null);

  const tryPlay = async () => {
    try {
      // Re-use cached audio if available (avoid double TTS call)
      if (cachedB64.current) {
        const audio = new Audio(`data:audio/mp3;base64,${cachedB64.current}`);
        await audio.play();
        setAudioPlaying(true); setAudioBlocked(false);
        audio.onended = () => setAudioPlaying(false);
        return;
      }
      const res = await axios.post(`${API}/assessment/speak`, {
        text: "Welcome to English Test UK. I'm Sandra, your AI examiner. Let's get you set up for your free speaking assessment.",
      });
      const b64 = res.data?.audio_b64;
      if (!b64) return;
      cachedB64.current = b64;
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      await audio.play();
      setAudioPlaying(true); setAudioBlocked(false);
      audio.onended = () => setAudioPlaying(false);
    } catch (e) {
      // Either TTS endpoint failed OR browser blocked autoplay
      setAudioBlocked(true);
    }
  };

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!spoke.current) {
        spoke.current = true;
        tryPlay();  // may be blocked by browser autoplay policy
      }
      for (let li = 0; li < heroLines.length; li++) {
        for (let i = 0; i < heroLines[li].length; i++) {
          if (stop) return;
          setTyped((t) => { const c = [...t]; c[li] = heroLines[li].slice(0, i + 1); return c; });
          await new Promise((r) => setTimeout(r, 38));
        }
        setStep(li + 1);
        await new Promise((r) => setTimeout(r, 350));
      }
    })();
    return () => { stop = true; };
  }, [heroLines]); // eslint-disable-line

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6 py-10 overflow-hidden"
      style={{ background: T.bg }} data-testid="welcome-stage">
      <style>{`@keyframes glowOrb { 0%,100% { opacity: 0.5 } 50% { opacity: 1 } }`}</style>
      {/* Background ambient glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-30"
          style={{ background: `radial-gradient(circle, ${T.primary}, transparent 60%)`, filter: "blur(80px)", animation: "glowOrb 6s ease-in-out infinite" }} />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: `radial-gradient(circle, ${T.primary}, transparent 60%)`, filter: "blur(80px)", animation: "glowOrb 8s ease-in-out infinite reverse" }} />
      </div>
      <ScanLines />

      <div className="relative z-10 max-w-4xl w-full text-center">
        {/* Speaker / Tap-for-sound indicator */}
        {audioBlocked ? (
          <button onClick={tryPlay} data-testid="welcome-enable-sound"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8 transition-all hover:-translate-y-0.5"
            style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}`, animation: "ttyGlow 2.5s infinite" }}>
            <Volume2 className="w-4 h-4" />
            <span className="font-mono text-[12px] tracking-[0.3em] uppercase">TAP TO HEAR SANDRA</span>
          </button>
        ) : (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(247,127,0,0.1)", border: `1px solid ${T.border}` }} data-testid="welcome-speaking-badge">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping" style={{ background: T.primary }} />
              <span className="relative inline-flex w-2.5 h-2.5 rounded-full" style={{ background: T.primary }} />
            </span>
            <Volume2 className="w-4 h-4" style={{ color: T.primary }} />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>
              {audioPlaying ? "SANDRA IS SPEAKING…" : "READY"}
            </span>
          </div>
        )}

        {/* Big typed welcome */}
        <h1 className="font-black leading-[1.1] text-5xl md:text-6xl lg:text-7xl mb-3"
          style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text, letterSpacing: "-0.02em" }}
          data-testid="welcome-h1">
          {typed[0]}
          {step === 0 && <Cursor big />}
        </h1>
        <h2 className="font-mono text-2xl md:text-3xl lg:text-4xl leading-snug"
          style={{ color: T.primary, textShadow: `0 0 24px ${T.primaryGlow}` }} data-testid="welcome-h2">
          {typed[1]}
          {step === 1 && <Cursor big />}
        </h2>

        <p className="mt-8 font-mono text-base md:text-lg max-w-2xl mx-auto" style={{ color: T.textDim }}>
          A short 5-minute voice assessment. Honest IELTS-style banding · instant predicted score · zero card details.
        </p>

        {/* CTA */}
        {step >= 2 && (
          <button onClick={onContinue} data-testid="welcome-continue"
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 font-mono text-base tracking-[0.3em] uppercase rounded-sm transition-all hover:-translate-y-1"
            style={{ background: T.primary, color: "#000", boxShadow: `0 0 30px ${T.primaryGlow}`, animation: "ttyGlow 2.5s infinite, ttyHeroFade 0.6s ease-out" }}>
            <Headphones className="w-5 h-5" /> Test mic & sound <ChevronRight className="w-5 h-5" />
          </button>
        )}

        <div className="mt-10 font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.textDim }}>
          // Use headphones for the best experience · You'll need to allow microphone access
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Stage 0.5 — MIC + SPEAKER TEST modal
// =================================================================
function MicCheckModal({ onCancel, onConfirm }) {
  const [phase, setPhase] = useState("speaker"); // speaker | speaker-confirm | mic | mic-listening | mic-confirm | done
  const [vol, setVol] = useState(0);
  const [maxVol, setMaxVol] = useState(0);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const playTone = async () => {
    setPhase("speaker-confirm");
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.setValueAtTime(660, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(990, ctx.currentTime + 0.4);
      o.frequency.exponentialRampToValueAtTime(550, ctx.currentTime + 0.8);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.0);
      o.start(); o.stop(ctx.currentTime + 1.0);
    } catch (e) { /* ignore */ }
  };

  const startMicTest = async () => {
    setPhase("mic-listening");
    setMaxVol(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx2.createMediaStreamSource(stream);
      const analyser = ctx2.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      let lastMax = 0;
      const loop = () => {
        analyser.getByteTimeDomainData(data);
        let m = 0;
        for (let i = 0; i < data.length; i++) m = Math.max(m, Math.abs(data[i] - 128));
        const v = m / 128;
        setVol(v);
        lastMax = Math.max(lastMax, v);
        setMaxVol(lastMax);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
      // After 4 seconds auto-evaluate
      setTimeout(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        setPhase("mic-confirm");
      }, 4500);
    } catch (e) {
      setPhase("mic-denied");
    }
  };

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(11,12,10,0.92)", backdropFilter: "blur(10px)" }}
      data-testid="mic-check-modal">
      <div className="relative w-full max-w-xl rounded-sm p-8"
        style={{ background: T.panel, border: `1.5px solid ${T.border}`, boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 30px ${T.primaryGlow}` }}>
        <HudCorners />
        <ScanLines />

        <div className="relative">
          <div className="flex items-center justify-between mb-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>// PRE-FLIGHT CHECK</div>
            <button onClick={onCancel} data-testid="mic-check-close" className="hover:opacity-100 opacity-60">
              <X className="w-5 h-5" style={{ color: T.text }} />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-7">
            <div className="flex-1 h-1 rounded-full" style={{ background: phase.startsWith("speaker") || phase !== "mic-denied" ? T.primary : T.border }} />
            <div className="flex-1 h-1 rounded-full" style={{ background: ["mic", "mic-listening", "mic-confirm", "done"].includes(phase) ? T.primary : "rgba(247,127,0,0.2)" }} />
          </div>

          {/* SPEAKER step */}
          {(phase === "speaker" || phase === "speaker-confirm") && (
            <div data-testid="mic-check-speaker">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: T.primary, color: "#000", boxShadow: `0 0 16px ${T.primaryGlow}` }}>
                  <Volume2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-black text-2xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>Speaker test</div>
                  <div className="font-mono text-sm" style={{ color: T.textDim }}>Click play — you should hear a short orange chime.</div>
                </div>
              </div>
              {phase === "speaker" && (
                <button onClick={playTone} data-testid="mic-check-play-tone"
                  className="w-full mt-4 px-6 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm transition-all"
                  style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}` }}>
                  <Play className="w-5 h-5 inline mr-2" /> Play test tone
                </button>
              )}
              {phase === "speaker-confirm" && (
                <div className="mt-4 space-y-2">
                  <div className="font-mono text-sm mb-2" style={{ color: T.text }}>Did you hear it?</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setPhase("mic")} data-testid="mic-check-speaker-yes"
                      className="px-4 py-3 font-mono text-sm tracking-[0.2em] uppercase rounded-sm"
                      style={{ background: T.success, color: "#fff" }}>
                      <Check className="w-4 h-4 inline mr-1.5" /> Yes, continue
                    </button>
                    <button onClick={playTone} data-testid="mic-check-speaker-replay"
                      className="px-4 py-3 font-mono text-sm tracking-[0.2em] uppercase rounded-sm"
                      style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.text }}>
                      Play again
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* MIC step */}
          {(phase === "mic" || phase === "mic-listening" || phase === "mic-confirm" || phase === "mic-denied") && (
            <div data-testid="mic-check-mic">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: T.primary, color: "#000", boxShadow: `0 0 16px ${T.primaryGlow}` }}>
                  <Mic className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-black text-2xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>Microphone test</div>
                  <div className="font-mono text-sm" style={{ color: T.textDim }}>
                    {phase === "mic" && "We'll listen for 4 seconds — say 'one, two, three…' out loud."}
                    {phase === "mic-listening" && "Listening… speak now."}
                    {phase === "mic-confirm" && (maxVol > 0.05 ? "Microphone detected." : "We didn't hear much — try again?")}
                    {phase === "mic-denied" && "Microphone permission denied. Please enable in browser settings and refresh."}
                  </div>
                </div>
              </div>

              {/* Live mic visualiser */}
              {(phase === "mic-listening" || phase === "mic-confirm") && (
                <div className="mt-3 flex items-center justify-center gap-1 h-20" data-testid="mic-check-visualizer">
                  {Array.from({ length: 28 }).map((_, i) => {
                    const seed = ((Math.sin(i * 0.6) + 1) / 2);
                    const live = phase === "mic-listening" ? vol * (0.6 + seed * 0.4) : Math.min(1, maxVol * (0.6 + seed * 0.4));
                    const h = Math.max(6, live * 80);
                    return (
                      <div key={i} className="rounded-sm" style={{
                        width: 6, height: h,
                        background: T.primary,
                        boxShadow: `0 0 ${4 + live * 12}px ${T.primaryGlow}`,
                        transition: "height 60ms linear",
                      }} />
                    );
                  })}
                </div>
              )}

              {phase === "mic" && (
                <button onClick={startMicTest} data-testid="mic-check-start"
                  className="w-full mt-3 px-6 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm"
                  style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}` }}>
                  <Mic className="w-5 h-5 inline mr-2" /> Start mic check
                </button>
              )}
              {phase === "mic-confirm" && maxVol > 0.05 && (
                <button onClick={onConfirm} data-testid="mic-check-confirm"
                  className="w-full mt-4 px-6 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm"
                  style={{ background: T.success, color: "#fff" }}>
                  <Check className="w-5 h-5 inline mr-2" /> All good · Begin assessment <ChevronRight className="w-5 h-5 inline" />
                </button>
              )}
              {phase === "mic-confirm" && maxVol <= 0.05 && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button onClick={startMicTest} data-testid="mic-check-retry"
                    className="px-4 py-3 font-mono text-sm tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.text }}>
                    Try again
                  </button>
                  <button onClick={onConfirm} data-testid="mic-check-skip"
                    className="px-4 py-3 font-mono text-sm tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: T.primary, color: "#000" }}>
                    Skip · continue anyway
                  </button>
                </div>
              )}
              {phase === "mic-denied" && (
                <div className="mt-4 px-4 py-3 rounded-sm font-mono text-sm flex items-start gap-2"
                  style={{ background: "rgba(214,40,40,0.1)", color: T.danger, border: `1px solid rgba(214,40,40,0.3)` }}>
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Open your browser settings, allow microphone for this site, then refresh the page.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Modal — beautiful email / phone input
// =================================================================
function FieldModal({ open, type, country, onClose, onSubmit }) {
  const [value, setValue] = useState("");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { if (open) { setValue(""); setErr(""); setTimeout(() => inputRef.current?.focus(), 50); } }, [open, type]);
  if (!open) return null;

  const isCountry = type === "country";
  const isEmail = type === "email";
  const Icon = isCountry ? Volume2 /* placeholder, replaced below */ : (isEmail ? Mail : PhoneIcon);

  const submit = () => {
    const v = value.trim();
    if (isEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setErr("Please enter a valid email address."); return; }
      onSubmit(v.toLowerCase(), v);
    } else if (type === "phone") {
      const digits = v.replace(/[^\d]/g, "");
      if (digits.length < 6) { setErr("Please enter at least 6 digits."); return; }
      onSubmit(digits, `${country?.dial || ""} ${digits}`);
    }
  };

  // Country picker modal — searchable dropdown list
  if (isCountry) {
    return <CountrySearchModal onClose={onClose} onSubmit={onSubmit} />;
  }

  // Email / Phone modal

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(11,12,10,0.92)", backdropFilter: "blur(10px)" }}
      data-testid={`field-modal-${type}`}>
      <div className="relative w-full max-w-lg rounded-sm p-8"
        style={{ background: T.panel, border: `1.5px solid ${T.border}`, boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 30px ${T.primaryGlow}` }}>
        <HudCorners />
        <ScanLines />
        <div className="relative">
          <div className="flex items-center justify-between mb-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>
              // {isEmail ? "EMAIL ADDRESS" : "PHONE NUMBER"}
            </div>
            <button onClick={onClose} data-testid={`field-modal-close-${type}`} className="opacity-60 hover:opacity-100">
              <X className="w-5 h-5" style={{ color: T.text }} />
            </button>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: T.primary, color: "#000", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="font-black text-3xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                {isEmail ? "Your email" : "Your phone"}
              </div>
              <div className="font-mono text-sm" style={{ color: T.textDim }}>
                {isEmail ? "We'll send your portal credentials here." : "Used only for account recovery."}
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 px-4 py-4 rounded-sm"
            style={{ background: "rgba(247,127,0,0.06)", border: `1.5px solid ${T.border}` }}>
            {!isEmail && (
              <span className="font-mono text-xl font-bold px-2 py-1 rounded-sm" style={{ color: T.primary, background: "rgba(247,127,0,0.1)" }}>
                {country?.dial || "+44"}
              </span>
            )}
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => { setValue(isEmail ? e.target.value : e.target.value.replace(/[^\d ]/g, "")); setErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              type={isEmail ? "email" : "tel"}
              inputMode={isEmail ? "email" : "tel"}
              placeholder={isEmail ? "you@example.com" : "20 1234 5678"}
              data-testid={`field-modal-input-${type}`}
              className="flex-1 bg-transparent outline-none font-mono text-xl py-1"
              style={{ color: T.text, caretColor: T.primary }}
              autoComplete="off"
            />
          </div>
          {err && (
            <div className="mt-2 font-mono text-sm flex items-center gap-1.5" style={{ color: T.danger }}>
              <AlertCircle className="w-4 h-4" /> {err}
            </div>
          )}
          <button onClick={submit} data-testid={`field-modal-submit-${type}`}
            className="w-full mt-6 px-6 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm transition-all"
            style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}` }}>
            Confirm <ChevronRight className="w-5 h-5 inline ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Searchable country dropdown (modal)
function CountrySearchModal({ onClose, onSubmit }) {
  const [q, setQ] = useState("");
  const searchRef = useRef(null);
  useEffect(() => { setTimeout(() => searchRef.current?.focus(), 80); }, []);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRY_CODES;
    return COUNTRY_CODES.filter((c) =>
      c.name.toLowerCase().includes(s) ||
      c.code.toLowerCase().includes(s) ||
      c.dial.includes(s)
    );
  }, [q]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(11,12,10,0.92)", backdropFilter: "blur(10px)" }}
      data-testid="field-modal-country">
      <div className="relative w-full max-w-lg rounded-sm p-7"
        style={{ background: T.panel, border: `1.5px solid ${T.border}`, boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 30px ${T.primaryGlow}` }}>
        <HudCorners />
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>// COUNTRY</div>
            <button onClick={onClose} data-testid="field-modal-close-country" className="opacity-60 hover:opacity-100">
              <X className="w-5 h-5" style={{ color: T.text }} />
            </button>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl"
              style={{ background: T.primary, color: "#000", boxShadow: `0 0 14px ${T.primaryGlow}` }}>🌐</div>
            <div>
              <div className="font-black text-2xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                Where are you based?
              </div>
              <div className="font-mono text-xs" style={{ color: T.textDim }}>Start typing to filter — name, code or dial.</div>
            </div>
          </div>

          {/* Search input */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.textDim }} />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country, code or dial…"
              data-testid="country-search-input"
              className="w-full pl-10 pr-3 py-3 rounded-sm font-mono text-base outline-none"
              style={{ background: "rgba(247,127,0,0.06)", border: `1.5px solid ${T.border}`, color: T.text, caretColor: T.primary }}
              autoComplete="off"
            />
          </div>

          {/* Results list */}
          <div className="mt-3 max-h-[340px] overflow-y-auto pr-1" data-testid="field-modal-country-options">
            {list.length === 0 ? (
              <div className="py-6 text-center font-mono text-sm" style={{ color: T.textDim }}>
                // No match for "{q}"
              </div>
            ) : list.map((c) => (
              <button key={c.code}
                onClick={() => onSubmit(c, `${c.flag} ${c.name} (${c.dial})`)}
                data-testid={`country-${c.code}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left transition-colors"
                onMouseEnter={(ev) => { ev.currentTarget.style.background = "rgba(247,127,0,0.12)"; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.background = "transparent"; }}>
                <span className="text-xl">{c.flag}</span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] opacity-60" style={{ color: T.textDim }}>{c.code}</span>
                <span className="font-mono text-base flex-1" style={{ color: T.text }}>{c.name}</span>
                <span className="font-mono text-base font-bold" style={{ color: T.primary }}>{c.dial}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-right" style={{ color: T.textDim }}>
            {list.length} of {COUNTRY_CODES.length}
          </div>
        </div>
      </div>
    </div>
  );
}

// Exam picker modal
function ExamModal({ open, onClose, onSubmit }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(11,12,10,0.92)", backdropFilter: "blur(10px)" }}
      data-testid="field-modal-exam">
      <div className="relative w-full max-w-xl rounded-sm p-8"
        style={{ background: T.panel, border: `1.5px solid ${T.border}`, boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 30px ${T.primaryGlow}` }}>
        <HudCorners />
        <ScanLines />
        <div className="relative">
          <div className="flex items-center justify-between mb-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>// TARGET EXAM</div>
            <button onClick={onClose} data-testid="field-modal-close-exam" className="opacity-60 hover:opacity-100">
              <X className="w-5 h-5" style={{ color: T.text }} />
            </button>
          </div>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
              style={{ background: T.primary, color: "#000", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
              🎯
            </div>
            <div>
              <div className="font-black text-3xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                Which exam?
              </div>
              <div className="font-mono text-sm" style={{ color: T.textDim }}>
                We'll tailor the assessment criteria to your target test.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {EXAM_OPTIONS.map((e) => (
              <button key={e.id} onClick={() => onSubmit(e.id, `${e.label} → selected`)}
                data-testid={`exam-${e.id}`}
                className="font-mono text-base tracking-[0.2em] uppercase px-4 py-4 rounded-sm transition-all hover:-translate-y-0.5"
                style={{ background: "rgba(247,127,0,0.06)", border: `1px solid ${T.border}`, color: T.text }}
                onMouseEnter={(ev) => { ev.currentTarget.style.background = T.primary; ev.currentTarget.style.color = "#000"; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.background = "rgba(247,127,0,0.06)"; ev.currentTarget.style.color = T.text; }}>
                {e.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Email + OTP modal (verify before account creation)
function EmailOtpModal({ open, onClose, onVerified, onAlreadyAssessed }) {
  const [phase, setPhase] = useState("email"); // email | sending | otp | verifying | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => { if (open) { setPhase("email"); setEmail(""); setOtp(""); setErr(""); setDevOtp(null); setTimeout(() => inputRef.current?.focus(), 60); } }, [open]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  if (!open) return null;

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setErr("Please enter a valid email address."); return; }
    setPhase("sending");
    try {
      const res = await axios.post(`${API}/assessment/send-otp`, { email: v });
      setDevOtp(res.data?.dev_otp || null);
      setPhase("otp");
      setCooldown(30);
      setTimeout(() => inputRef.current?.focus(), 80);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Could not send code.";
      // 409 → this email has already taken / started / terminated the assessment.
      // Fetch the result and bubble up so the parent renders the terminated/completed view.
      if (status === 409 && onAlreadyAssessed) {
        try {
          const r = await axios.get(`${API}/assessment/by-email`, { params: { email: v } });
          onAlreadyAssessed(v, r.data || null, msg);
          return;
        } catch (_e) {
          onAlreadyAssessed(v, null, msg);
          return;
        }
      }
      setErr(msg + " — please tap SEND CODE again or refresh the page.");
      setPhase("email");
    }
  };

  const verify = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(otp.trim())) { setErr("Code must be 6 digits."); return; }
    setPhase("verifying");
    try {
      await axios.post(`${API}/assessment/verify-otp`, { email: email.trim().toLowerCase(), code: otp.trim() });
      setPhase("done");
      setTimeout(() => onVerified(email.trim().toLowerCase()), 500);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Verification failed.";
      setErr(msg);
      setPhase("otp");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ background: "rgba(11,12,10,0.92)", backdropFilter: "blur(10px)" }}
      data-testid="email-otp-modal">
      <div className="relative w-full max-w-lg rounded-sm p-8"
        style={{ background: T.panel, border: `1.5px solid ${T.border}`, boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 30px ${T.primaryGlow}` }}>
        <HudCorners />
        <ScanLines />
        <div className="relative">
          <div className="flex items-center justify-between mb-5">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>
              // {phase === "email" ? "VERIFY YOUR EMAIL" : "ENTER 6-DIGIT CODE"}
            </div>
            <button onClick={onClose} data-testid="email-otp-close" className="opacity-60 hover:opacity-100">
              <X className="w-5 h-5" style={{ color: T.text }} />
            </button>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: T.primary, color: "#000", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
              <Mail className="w-6 h-6" />
            </div>
            <div>
              <div className="font-black text-2xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                {phase === "email" ? "Your email" : `Check ${email}`}
              </div>
              <div className="font-mono text-sm" style={{ color: T.textDim }}>
                {phase === "email"
                  ? "We'll send you a 6-digit code to confirm it's really you."
                  : "We've sent a 6-digit code. Enter it below to continue."}
              </div>
            </div>
          </div>

          {(phase === "email" || phase === "sending") && (
            <form onSubmit={sendOtp}>
              <div className="flex items-center gap-2 px-4 py-4 rounded-sm"
                style={{ background: "rgba(247,127,0,0.06)", border: `1.5px solid ${T.border}` }}>
                <input
                  ref={inputRef}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErr(""); }}
                  type="email" inputMode="email" placeholder="you@example.com"
                  data-testid="otp-email-input"
                  className="flex-1 bg-transparent outline-none font-mono text-xl py-1"
                  style={{ color: T.text, caretColor: T.primary }} autoComplete="email" />
              </div>
              {err && <div className="mt-2 font-mono text-sm flex items-center gap-1.5" style={{ color: T.danger }}><AlertCircle className="w-4 h-4" /> {err}</div>}
              <button type="submit" disabled={phase === "sending"} data-testid="otp-send-btn"
                className="w-full mt-6 px-6 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm transition-all disabled:opacity-60"
                style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}` }}>
                {phase === "sending" ? <><Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Sending…</> : <>Send code <ChevronRight className="w-5 h-5 inline ml-1" /></>}
              </button>
            </form>
          )}

          {(phase === "otp" || phase === "verifying" || phase === "done") && (
            <form onSubmit={verify}>
              <div className="flex items-center justify-center gap-2 px-4 py-4 rounded-sm"
                style={{ background: "rgba(247,127,0,0.06)", border: `1.5px solid ${T.border}` }}>
                <input
                  ref={inputRef}
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/[^\d]/g, "").slice(0,6)); setErr(""); }}
                  inputMode="numeric" pattern="[0-9]*" placeholder="••••••" maxLength={6}
                  data-testid="otp-code-input"
                  className="text-center bg-transparent outline-none font-mono py-1"
                  style={{ color: T.text, caretColor: T.primary, fontSize: 36, letterSpacing: "0.5em", width: "100%" }} autoComplete="one-time-code" />
              </div>
              {devOtp && (
                <div className="mt-2 px-3 py-2 rounded-sm font-mono text-xs flex items-center gap-2"
                  style={{ background: "rgba(247,127,0,0.08)", border: `1px dashed ${T.border}`, color: T.primary }}
                  data-testid="otp-dev-hint">
                  DEV-MODE · code: <strong>{devOtp}</strong> (real email not configured yet)
                </div>
              )}
              {err && <div className="mt-2 font-mono text-sm flex items-center gap-1.5" style={{ color: T.danger }}><AlertCircle className="w-4 h-4" /> {err}</div>}
              <button type="submit" disabled={phase === "verifying" || phase === "done"} data-testid="otp-verify-btn"
                className="w-full mt-6 px-6 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm transition-all disabled:opacity-60"
                style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}` }}>
                {phase === "verifying" ? <><Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Verifying…</> :
                 phase === "done" ? <><Check className="w-4 h-4 inline mr-2" /> Verified</> :
                 <>Verify <ChevronRight className="w-5 h-5 inline ml-1" /></>}
              </button>
              <div className="mt-4 flex items-center justify-between font-mono text-xs">
                <button type="button" onClick={() => setPhase("email")} data-testid="otp-change-email"
                  className="opacity-70 hover:opacity-100" style={{ color: T.text }}>
                  ← Change email
                </button>
                <button type="button" onClick={sendOtp} disabled={cooldown > 0} data-testid="otp-resend"
                  className="opacity-70 hover:opacity-100 disabled:opacity-40" style={{ color: T.primary }}>
                  Resend code{cooldown > 0 ? ` (${cooldown}s)` : ""}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Stage 1 — BOOT
// =================================================================
function BootStage({ onDone }) {
  const lines = useMemo(() => ([
    { who: "SYS", text: "Initialising secure session…  OK" },
    { who: "SYS", text: "Loading examiner profile [SANDRA · IELTS Senior · British]…  OK" },
    { who: "SYS", text: "Mic permissions: granted · TTS: nova · STT: whisper-1" },
    { who: "AI",  text: "Great — mic and sound are working. Let me grab a few details, then we'll begin." },
    { who: "AI",  text: "Type each one below or use the popup when it appears." },
  ]), []);
  const { rendered, typing } = useTypewriter(lines, 12, () => setTimeout(() => onDone(lines), 300));
  return (
    <>
      {rendered.map((l, i) => <Line key={i} line={l} isLast={i === rendered.length - 1 && typing} />)}
    </>
  );
}

// =================================================================
// Stage 2 — COLLECT
// =================================================================
function CollectStage({ push, onDone, onAlreadyAssessed }) {
  const STEPS = ["full_name", "email", "country", "phone", "target_exam"];
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState({ full_name: "", email: "", country: COUNTRY_CODES[0], phone: "", target_exam: "" });
  const [prompted, setPrompted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const prompts = {
    full_name: "Let's start. What's your full name?",
    email: "Got it. Your email address please — I'll use this to send your portal credentials.",
    country: "Where are you based? Pick a country (we use this to set your local number).",
    phone: "And your phone number (digits only — we'll add the country code).",
    target_exam: "Which exam are you preparing for?",
  };

  useEffect(() => {
    if (prompted) return;
    push([{ who: "AI", text: prompts[STEPS[stepIdx]] }]);
    setPrompted(true);
    // Auto-open modals for email, country, phone, and exam steps after a short delay
    if (["email", "country", "phone", "target_exam"].includes(STEPS[stepIdx])) {
      setTimeout(() => setModalOpen(true), 800);
    }
  }, [stepIdx, prompted]); // eslint-disable-line

  const advance = (key, value, echo) => {
    push([{ who: "USR", text: echo }]);
    setForm((f) => ({ ...f, [key]: value }));
    setPrompted(false);
    setModalOpen(false);
    if (stepIdx + 1 >= STEPS.length) onDone({ ...form, [key]: value });
    else setStepIdx(stepIdx + 1);
  };

  const step = STEPS[stepIdx];

  return (
    <div className="mt-3" data-testid={`collect-step-${step}`}>
      {step === "full_name" && (
        <NameInput onSubmit={(v) => advance("full_name", v, v)} />
      )}

      {step === "email" && (
        <>
          <button onClick={() => setModalOpen(true)} data-testid="open-email-modal"
            className="font-mono text-base tracking-[0.2em] uppercase px-4 py-2 rounded-sm"
            style={{ background: "rgba(247,127,0,0.1)", border: `1px solid ${T.border}`, color: T.primary }}>
            <Mail className="w-4 h-4 inline mr-2" /> Open email input
          </button>
          <EmailOtpModal open={modalOpen} onClose={() => setModalOpen(false)}
            onVerified={(v) => advance("email", v, `${v} ✓ verified`)}
            onAlreadyAssessed={onAlreadyAssessed} />
        </>
      )}

      {step === "country" && (
        <>
          <button onClick={() => setModalOpen(true)} data-testid="open-country-modal"
            className="font-mono text-base tracking-[0.2em] uppercase px-4 py-2 rounded-sm"
            style={{ background: "rgba(247,127,0,0.1)", border: `1px solid ${T.border}`, color: T.primary }}>
            🌐 Open country picker
          </button>
          <FieldModal open={modalOpen} type="country" onClose={() => setModalOpen(false)}
            onSubmit={(c, echo) => advance("country", c, echo)} />
        </>
      )}

      {step === "phone" && (
        <>
          <button onClick={() => setModalOpen(true)} data-testid="open-phone-modal"
            className="font-mono text-base tracking-[0.2em] uppercase px-4 py-2 rounded-sm"
            style={{ background: "rgba(247,127,0,0.1)", border: `1px solid ${T.border}`, color: T.primary }}>
            <PhoneIcon className="w-4 h-4 inline mr-2" /> Open phone input
          </button>
          <FieldModal open={modalOpen} type="phone" country={form.country} onClose={() => setModalOpen(false)}
            onSubmit={(v, echo) => advance("phone", v, echo)} />
        </>
      )}

      {step === "target_exam" && (
        <>
          <button onClick={() => setModalOpen(true)} data-testid="open-exam-modal"
            className="font-mono text-base tracking-[0.2em] uppercase px-4 py-2 rounded-sm"
            style={{ background: "rgba(247,127,0,0.1)", border: `1px solid ${T.border}`, color: T.primary }}>
            🎯 Open exam picker
          </button>
          <ExamModal open={modalOpen} onClose={() => setModalOpen(false)}
            onSubmit={(id, echo) => advance("target_exam", id, echo)} />
        </>
      )}
    </div>
  );
}

function NameInput({ onSubmit }) {
  const [text, setText] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-3" data-testid="prompt-line-full_name">
      <span className="font-mono text-2xl" style={{ color: T.primary }}>$</span>
      <input
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && text.trim().length >= 2) onSubmit(text.trim()); }}
        data-testid="prompt-input"
        placeholder="Type your full name…"
        className="flex-1 bg-transparent outline-none font-mono text-xl lg:text-2xl py-1"
        style={{ color: T.text, caretColor: T.primary }}
        autoComplete="off"
      />
      <Cursor big />
    </div>
  );
}

// =================================================================
// Stage 3 — CREATE_ACCOUNT
// =================================================================
function CreateAccountStage({ push, form, onDone }) {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; ran.current = true;
    (async () => {
      push([{ who: "SYS", text: "Provisioning portal access… please wait." }]);
      try {
        // No `withCredentials` — the access_token comes back in the JSON
        // body and is stored in localStorage. Avoiding credentialed mode
        // sidesteps the CORS `Allow-Origin: *` + credentials conflict that
        // can manifest as a generic "Network Error" in production.
        const res = await axios.post(`${API}/assessment/quick-create`, {
          full_name: form.full_name, email: form.email,
          country_code: form.country.code, country_name: form.country.name,
          phone: form.phone, target_exam: form.target_exam,
        });
        const { password, already_existed, email } = res.data;
        if (already_existed) {
          push([
            { who: "SYS", text: "Account found · linked to this session." },
            { who: "AI", text: `Welcome back. I've linked this session to your existing account (${email}). Use your existing password to log in any time.` },
          ]);
        } else {
          push([
            { who: "SYS", text: "✓ Account created · ✓ Lead recorded · ✓ Welcome email queued" },
            { who: "AI",  text: "Your portal access is ready. Save these credentials — I've also emailed them to you:" },
            { who: "SYS", text: `   USERNAME : ${email}` },
            { who: "SYS", text: `   PASSWORD : ${password}` },
            { who: "AI",  text: "You can change the password any time from the Profile page once you log in." },
          ]);
        }
        await new Promise((r) => setTimeout(r, 1200));
        onDone({ email, password: password || null, already_existed });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e.message;
        if (status === 409) {
          push([
            { who: "SYS", text: `× ${detail}` },
            { who: "AI", text: "You've already taken your free assessment. To continue practising and lift your band, please subscribe to one of our prep plans." },
            { who: "SYS", text: "Redirecting to pricing in 4 seconds…" },
          ]);
          setTimeout(() => { window.location.href = "/pricing"; }, 4000);
        } else {
          push([{ who: "SYS", text: `× Error: ${detail}. Please refresh and try again.` }]);
        }
      }
    })();
    // eslint-disable-next-line
  }, []);
  return null;
}

// =================================================================
// Stage 4 — INSTRUCTIONS
// =================================================================
function InstructionsStage({ push, onStart }) {
  const [ready, setReady] = useState(false);
  const lines = useMemo(() => ([
    { who: "AI",  text: "Here's how the next 5 minutes will work:" },
    { who: "SYS", text: "  ▸ 3 questions · 60 seconds of speaking each" },
    { who: "SYS", text: "  ▸ I'll show the question AND read it out loud to you" },
    { who: "SYS", text: "  ▸ When the red dot appears, start speaking — recording auto-stops at 60s" },
    { who: "SYS", text: "  ▸ At the end you'll see your IELTS-style predicted band (honest, not inflated)" },
    { who: "AI",  text: "Find a quiet spot. Speak naturally — long pauses cost you marks. Ready?" },
  ]), []);
  const { rendered, typing } = useTypewriter(lines, 12, () => setReady(true));
  return (
    <>
      {rendered.map((l, i) => <Line key={i} line={l} isLast={i === rendered.length - 1 && typing} />)}
      {ready && (
        <div className="mt-5">
          <button onClick={() => { push(lines); onStart(); }} data-testid="instructions-start"
            className="inline-flex items-center gap-2 px-7 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm transition-all"
            style={{ background: T.primary, color: "#000", boxShadow: `0 0 24px ${T.primaryGlow}`, animation: "ttyGlow 2.5s infinite" }}>
            <Mic className="w-5 h-5" /> Begin Speaking Assessment <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
}

// =================================================================
// Stage 5a — CONNECTING animation (full-screen)
// =================================================================
function ConnectingStage({ ctx, onDone }) {
  const [step, setStep] = useState(0);
  const examinerId = useMemo(() => ctx._examinerId || `EX-${Math.floor(1000 + Math.random() * 8999)}`, [ctx._examinerId]);
  const roomNo = useMemo(() => ctx._roomNo || Math.floor(100 + Math.random() * 899), [ctx._roomNo]);
  const candidateId = useMemo(() => ctx._candidateId || `CAND-${Date.now().toString().slice(-6)}`, [ctx._candidateId]);

  // Persist generated IDs on ctx so ExamRoomStage uses the SAME values
  useEffect(() => {
    if (!ctx._examinerId) ctx._examinerId = examinerId;
    if (!ctx._roomNo) ctx._roomNo = roomNo;
    if (!ctx._candidateId) ctx._candidateId = candidateId;
  }, []); // eslint-disable-line

  const lines = useMemo(() => ([
    "Establishing secure exam channel…",
    "Authenticating candidate identity…",
    `Examiner profile loaded — SANDRA · ${examinerId}`,
    `Assigning room — Room #${roomNo}`,
    "Configuring recording infrastructure…",
    "Ready. Joining room now…",
  ]), [examinerId, roomNo]);

  useEffect(() => {
    if (step >= lines.length) {
      const t = setTimeout(onDone, 900);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStep(step + 1), 600);
    return () => clearTimeout(t);
  }, [step, lines.length]); // eslint-disable-line

  const firstName = (ctx.full_name || "Candidate").split(" ")[0];

  return (
    <div className="fixed inset-0 flex items-center justify-center px-6" style={{ background: T.bg, color: T.text }}
      data-testid="connecting-stage">
      <style>{`@keyframes ring1 { 0%{transform:scale(.8);opacity:0} 50%{opacity:.7} 100%{transform:scale(2.2);opacity:0} }`}</style>
      <ScanLines />

      <div className="relative max-w-2xl w-full text-center">
        {/* Pulse rings around examiner avatar */}
        <div className="relative inline-block mb-8">
          <span className="absolute inset-0 rounded-full border-2" style={{ borderColor: T.primary, animation: "ring1 2.2s infinite" }} />
          <span className="absolute inset-0 rounded-full border-2" style={{ borderColor: T.primary, animation: "ring1 2.2s 0.5s infinite" }} />
          <span className="absolute inset-0 rounded-full border-2" style={{ borderColor: T.primary, animation: "ring1 2.2s 1.0s infinite" }} />
          <div className="relative w-28 h-28 rounded-full flex items-center justify-center"
            style={{ background: T.primary, color: "#000", boxShadow: `0 0 40px ${T.primaryGlow}` }}>
            <User className="w-14 h-14" />
          </div>
        </div>

        <div className="font-mono text-[11px] tracking-[0.4em] uppercase mb-1" style={{ color: T.primary }}>
          // EXAM PROTOCOL · CHANNEL HANDSHAKE
        </div>
        <h1 className="font-black text-4xl md:text-5xl mb-1" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text, letterSpacing: "-0.02em" }}>
          Connecting to examination room…
        </h1>
        <p className="font-mono text-sm md:text-base" style={{ color: T.textDim }}>
          Hold on {firstName} — your AI examiner is connecting to your secure room.
        </p>

        {/* Steps */}
        <div className="mt-8 max-w-md mx-auto text-left space-y-2">
          {lines.map((l, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <div key={i} className="flex items-center gap-3 font-mono text-sm"
                style={{ color: done ? T.success : active ? T.primary : T.textDim, opacity: done ? 0.85 : active ? 1 : 0.4 }}>
                <span className="w-5 inline-flex justify-center">
                  {done ? <Check className="w-4 h-4" /> : active ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                </span>
                <span className="flex-1">{l}</span>
              </div>
            );
          })}
        </div>

        {/* Candidate badge */}
        <div className="mt-10 inline-flex items-center gap-3 px-5 py-3 rounded-sm"
          style={{ background: "rgba(247,127,0,0.06)", border: `1px solid ${T.border}` }}>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.textDim }}>CANDIDATE</div>
          <div className="font-mono text-sm" style={{ color: T.text }}>{ctx.full_name || "—"}</div>
          <div className="w-px h-5" style={{ background: T.border }} />
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.textDim }}>ID</div>
          <div className="font-mono text-sm" style={{ color: T.primary }}>{candidateId}</div>
          <div className="w-px h-5" style={{ background: T.border }} />
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.textDim }}>ROOM</div>
          <div className="font-mono text-sm" style={{ color: T.primary }}>#{roomNo}</div>
        </div>
      </div>
    </div>
  );
}

// =================================================================
// Stage 5b — EXAM ROOM (proper speaking-test interface)
// =================================================================
function ExamRoomStage({ ctx, nav }) {
  const firstName = (ctx.full_name || "Candidate").split(" ")[0];
  const candidateId = ctx._candidateId || `CAND-${Date.now().toString().slice(-6)}`;

  // Randomised question set — fetched once on mount from /api/assessment/questions.
  // Falls back to the small hard-coded QUESTIONS array if the fetch fails so the
  // candidate never sees a broken room.
  const [questionSet, setQuestionSet] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/assessment/questions`,
          { params: { target_exam: ctx.target_exam || "IELTS" } });
        if (!cancelled && Array.isArray(r.data) && r.data.length === 3) {
          setQuestionSet(r.data);
        } else if (!cancelled) {
          setQuestionSet(QUESTIONS);
        }
      } catch (_e) {
        if (!cancelled) setQuestionSet(QUESTIONS);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const personalisedQuestions = useMemo(() => {
    const src = questionSet || QUESTIONS;
    return [
      { ...src[0], greet: `Hello ${firstName}, welcome to your IELTS speaking assessment. Let's start with a warm-up.` },
      { ...src[1], greet: `Great, ${firstName}. Now for a longer answer — take a moment to think before you speak.` },
      { ...src[2], greet: `Perfect. One final question for you, ${firstName}.` },
    ];
  }, [questionSet, firstName]);

  const [qIdx, setQIdx] = useState(0);
  const [phase, setPhase] = useState("greeting"); // greeting | get-ready | recording | grading | done
  const [seconds, setSeconds] = useState(60);
  const [readySecs, setReadySecs] = useState(5);
  const [vol, setVol] = useState(0);
  const [results, setResults] = useState([]);
  const [finalResult, setFinalResult] = useState(null);
  const [examinerSpeaking, setExaminerSpeaking] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const readyTimerRef = useRef(null);
  const phaseRef = useRef("greeting");
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Lock the lead the moment the candidate steps into the exam room AND
  // arm the terminate-on-exit beacon. Without this, closing the tab mid-interview
  // would let them retry from the CLI later.
  const terminateUrl = `${API}/assessment/terminate`;
  const studentEmail = (ctx.email || ctx.account?.email || "").toLowerCase();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!studentEmail) return;
      try {
        await axios.post(`${API}/assessment/mark-started`, {
          email: studentEmail,
          phone: ctx.phone || null,
          user_id: ctx.account?.user_id || null,
          target_exam: ctx.target_exam || "IELTS",
        });
      } catch (_) { /* non-fatal */ }
      if (cancelled) return;
    })();

    const beacon = (reason) => {
      try {
        if (phaseRef.current === "done" || !studentEmail) return;
        const blob = new Blob([JSON.stringify({ email: studentEmail, reason })],
          { type: "application/json" });
        if (navigator.sendBeacon) navigator.sendBeacon(terminateUrl, blob);
        else fetch(terminateUrl, { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: studentEmail, reason }), keepalive: true });
      } catch (_) {}
    };
    const onBefore = () => beacon("beforeunload");
    const onHidden = () => { if (document.visibilityState === "hidden") beacon("page_hidden"); };
    window.addEventListener("beforeunload", onBefore);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      cancelled = true;
      window.removeEventListener("beforeunload", onBefore);
      document.removeEventListener("visibilitychange", onHidden);
      // On React unmount during an active interview, also terminate.
      if (phaseRef.current !== "done") beacon("unmount");
    };
    // eslint-disable-next-line
  }, []);

  const Q = personalisedQuestions[qIdx];

  // Speak any text via the public /speak endpoint
  const sandraSay = async (text) => {
    setExaminerSpeaking(true);
    try {
      const res = await axios.post(`${API}/assessment/speak`, { text });
      const b64 = res.data?.audio_b64;
      if (b64) {
        const audio = new Audio(`data:audio/mp3;base64,${b64}`);
        audioRef.current = audio;
        try { await audio.play(); } catch (e) {}
        await new Promise((r) => { audio.onended = r; setTimeout(r, 16000); });
      }
    } catch (e) {} finally { setExaminerSpeaking(false); }
  };

  // On entering a new question, play the personalised greeting + question,
  // then start a 5-second "GET READY" countdown that auto-launches the
  // 60-second recording (no manual click anymore — full immersion).
  // Gated on questionSet being loaded so a slow /questions fetch never
  // races against Sandra reading out a placeholder question text.
  useEffect(() => {
    if (!questionSet) return;
    let cancelled = false;
    (async () => {
      setPhase("greeting");
      await sandraSay(`${Q.greet} ${Q.text}`);
      if (cancelled) return;
      setReadySecs(5);
      setPhase("get-ready");
      if (readyTimerRef.current) clearInterval(readyTimerRef.current);
      readyTimerRef.current = setInterval(() => {
        setReadySecs((s) => {
          if (s <= 1) {
            clearInterval(readyTimerRef.current);
            readyTimerRef.current = null;
            // Defer to next tick so React commits the 0 before we transition.
            setTimeout(() => { if (!cancelled) beginRecording(); }, 0);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    })();
    return () => {
      cancelled = true;
      if (readyTimerRef.current) { clearInterval(readyTimerRef.current); readyTimerRef.current = null; }
    };
    // eslint-disable-next-line
  }, [qIdx, questionSet]);

  const beginRecording = async () => {
    setPhase("recording");
    setSeconds(Q.seconds);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx2.createMediaStreamSource(stream);
      const analyser = ctx2.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteTimeDomainData(data);
        let max = 0;
        for (let i = 0; i < data.length; i++) max = Math.max(max, Math.abs(data[i] - 128));
        setVol(max / 128);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = onStop;
      mr.start();
      timerRef.current = setInterval(() => {
        setSeconds((s) => { if (s <= 1) { stopRecording(); return 0; } return s - 1; });
      }, 1000);
    } catch (e) {
      setPhase("mic-denied");
    }
  };

  const stopRecording = () => {
    try { if (recorderRef.current && recorderRef.current.state === "recording") recorderRef.current.stop(); } catch {}
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const onStop = async () => {
    setPhase("grading");
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    const fd = new FormData();
    fd.append("file", blob, `q${Q.n}.webm`);
    fd.append("question", Q.text);
    fd.append("q_index", String(Q.n));
    fd.append("target_exam", ctx.target_exam || "IELTS");
    try {
      const res = await axios.post(`${API}/assessment/grade-speaking`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const r = res.data;
      const item = { q_index: Q.n, question: Q.text, ...r };
      setResults((rs) => [...rs, item]);
      if (qIdx + 1 >= personalisedQuestions.length) {
        const finalRes = await axios.post(`${API}/assessment/finalize`, {
          target_exam: ctx.target_exam || "IELTS",
          email: ctx.email || ctx.account?.email,
          phone: ctx.phone,
          full_name: ctx.full_name,
          user_id: ctx.account?.user_id,
          items: [...results, item].map((x) => ({
            q_index: x.q_index, question: x.question, transcript: x.transcript || "",
            fluency: x.fluency, lexical: x.lexical, grammar: x.grammar, pronunciation: x.pronunciation, band: x.band,
          })),
        });
        // Show result inline INSIDE the exam room (no terminal page transition)
        setFinalResult({ ...finalRes.data, items: [...results, item] });
        setPhase("done");
        // Auto-play Sandra's spoken pitch — single fire-and-forget call.
        const pitch = finalRes.data?.voice_summary;
        if (pitch) { sandraSay(pitch).catch(() => {}); }
      } else {
        setTimeout(() => setQIdx(qIdx + 1), 1500);
      }
    } catch (e) {
      setPhase("error");
    }
  };

  useEffect(() => () => stopRecording(), []);

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: T.bg, color: T.text }}
      data-testid={`exam-room-q${Q.n}`}>
      {/* Top status bar */}
      <div className="flex items-center gap-4 px-6 py-3 border-b" style={{ borderColor: T.border, background: T.panel }}>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: T.danger, boxShadow: `0 0 10px ${T.danger}`, animation: "ttyBlink 1.4s infinite" }} />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: T.danger }}>LIVE</span>
        </div>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textDim }}>EXAM ROOM · IELTS SPEAKING</div>
        <div className="ml-auto flex items-center gap-4 font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textDim }}>
          <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {ctx.full_name}</div>
          <div className="flex items-center gap-1.5" style={{ color: T.primary }}>{candidateId}</div>
          <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Q{Q.n} OF {personalisedQuestions.length}</div>
        </div>
      </div>

      {/* Main room — examiner panel + candidate panel */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4 p-5 lg:p-7 overflow-hidden">
        {/* Examiner panel */}
        <div className="relative rounded-sm overflow-hidden flex flex-col"
          style={{ background: T.panel, border: `1px solid ${T.border}`, boxShadow: `0 0 24px rgba(0,0,0,0.4)` }}>
          <HudCorners />
          <ScanLines />

          {/* Examiner header */}
          <div className="relative flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: T.border }}>
            <div className="relative">
              <span className="absolute inset-0 rounded-full border-2" style={{
                borderColor: examinerSpeaking ? T.primary : "transparent",
                animation: examinerSpeaking ? "ring1 1.6s infinite" : "none",
              }} />
              <div className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: T.primary, color: "#000", boxShadow: `0 0 14px ${T.primaryGlow}` }}>
                <User className="w-6 h-6" />
              </div>
            </div>
            <div>
              <div className="font-black text-xl leading-none" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                Sandra · AI Examiner
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-1" style={{ color: examinerSpeaking ? T.primary : T.textDim }}>
                {examinerSpeaking ? <><Radio className="w-3 h-3 inline mr-1" /> SPEAKING…</> : phase === "recording" ? "LISTENING" : phase === "grading" ? "EVALUATING" : "WAITING"}
              </div>
            </div>
          </div>

          {/* Question card / Result panel */}
          <div className="relative flex-1 p-7 overflow-y-auto">
            {phase !== "done" && (
              <>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: T.primary }}>
                  // QUESTION {Q.n} · {Q.type.toUpperCase()}
                </div>
                {(phase === "greeting" || phase === "get-ready") && Q.greet && (
                  <div className="mb-3 font-mono text-base italic" style={{ color: T.primary }}>
                    "{Q.greet}"
                  </div>
                )}
                <div className="font-mono text-xl lg:text-2xl leading-relaxed" style={{ color: T.text }}>
                  {Q.text}
                </div>
                {phase === "grading" && (
                  <div className="font-mono text-base mt-3 flex items-center gap-2" style={{ color: T.primary }}>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sandra is evaluating your answer…
                  </div>
                )}
              </>
            )}

            {phase === "done" && finalResult && (
              <div data-testid="exam-room-result">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: T.primary }}>
                  // ASSESSMENT REPORT · STRICTLY VETTED
                </div>
                <h2 className="font-black text-3xl lg:text-4xl mb-4" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                  {firstName}, here is your honest result.
                </h2>

                {/* Criteria breakdown */}
                <div className="rounded-sm p-5 mb-4" style={{ background: "rgba(247,127,0,0.04)", border: `1px solid ${T.border}` }}>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: T.textDim }}>BAND BREAKDOWN</div>
                  {[
                    { k: "fluency",       l: "Fluency & Coherence" },
                    { k: "lexical",       l: "Lexical Resource" },
                    { k: "grammar",       l: "Grammatical Range" },
                    { k: "pronunciation", l: "Pronunciation" },
                  ].map((row) => (
                    <div key={row.k} className="flex items-center justify-between mt-2.5" data-testid={`band-${row.k}`}>
                      <div className="font-mono text-base" style={{ color: T.text }}>{row.l}</div>
                      <div className="font-black text-3xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.primary, textShadow: `0 0 14px ${T.primaryGlow}` }}>
                        {Number(finalResult.criteria?.[row.k] || 0).toFixed(1)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sandra speaks — voice-first. No written examiner note. */}
                {finalResult.voice_summary && (
                  <div className="rounded-sm p-6 mt-2 flex items-center gap-4"
                       style={{ background: "rgba(247,127,0,0.08)", border: `1.5px solid ${T.primary}`, boxShadow: `0 0 22px ${T.primaryGlow}` }}
                       data-testid="sandra-voice-pitch">
                    <div className="relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: T.primary, color: "#000",
                               boxShadow: examinerSpeaking ? `0 0 18px ${T.primaryGlow}` : "none" }}>
                      <Volume2 className="w-7 h-7" />
                      {examinerSpeaking && (
                        <span className="absolute inset-0 rounded-full border-2 border-white/50"
                          style={{ animation: "ttyPulseRing 1.4s infinite" }} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-mono text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: T.primary }}>
                        {examinerSpeaking ? "SANDRA IS SPEAKING…" : "MESSAGE FROM SANDRA"}
                      </div>
                      <div className="font-black text-xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                        {examinerSpeaking ? "Listen carefully to your result." : "Click play to hear your result again."}
                      </div>
                    </div>
                    {!examinerSpeaking && (
                      <button onClick={() => sandraSay(finalResult.voice_summary)} data-testid="replay-sandra-voice"
                        className="inline-flex items-center gap-2 px-4 py-3 font-mono text-xs tracking-[0.25em] uppercase rounded-sm flex-shrink-0"
                        style={{ background: "rgba(247,127,0,0.15)", border: `1px solid ${T.primary}`, color: T.primary }}>
                        <Play className="w-3.5 h-3.5" /> Play again
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Candidate panel (right column) */}
        <div className="relative rounded-sm overflow-hidden flex flex-col"
          style={{ background: T.panel, border: `1px solid ${T.border}` }}>
          <HudCorners />
          <ScanLines />
          <div className="relative flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: T.border }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: phase === "recording" ? T.danger : "rgba(247,127,0,0.1)", color: phase === "recording" ? "#fff" : T.primary, border: `1.5px solid ${T.border}` }}>
              <Mic className="w-6 h-6" />
            </div>
            <div>
              <div className="font-black text-xl leading-none" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                {ctx.full_name || "Candidate"}
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-1" style={{ color: T.textDim }}>
                CANDIDATE · {ctx.country?.name || "—"}
              </div>
            </div>
          </div>

          <div className="relative flex-1 p-5 flex flex-col">
            {/* Phase content */}
            {phase === "greeting" && (
              <div className="flex flex-col items-center justify-center flex-1 text-center" data-testid={`q${Q.n}-greeting`}>
                <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: T.primary }} />
                <div className="font-mono text-sm tracking-[0.2em] uppercase" style={{ color: T.primary }}>
                  SANDRA IS GREETING YOU…
                </div>
                <div className="font-mono text-xs mt-2" style={{ color: T.textDim }}>Please wait, your question is being read.</div>
              </div>
            )}

            {phase === "get-ready" && (
              <div className="flex flex-col items-center justify-center flex-1 text-center" data-testid={`q${Q.n}-get-ready`}>
                <div className="font-mono text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: T.primary }}>
                  GET READY · RECORDING STARTS IN
                </div>
                <div className="relative w-32 h-32 rounded-full flex items-center justify-center mb-4"
                  style={{ background: "rgba(247,127,0,0.08)", border: `2px solid ${T.primary}`, boxShadow: `0 0 36px ${T.primaryGlow}`, animation: "ttyPulseRing 1.4s infinite alternate" }}>
                  <div className="font-black leading-none" style={{ fontSize: 80, fontFamily: "'Rajdhani', sans-serif", color: T.primary, textShadow: `0 0 14px ${T.primaryGlow}` }} data-testid={`q${Q.n}-ready-countdown`}>
                    {readySecs}
                  </div>
                </div>
                <div className="font-black text-2xl mb-2" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                  Ready, {firstName}?
                </div>
                <div className="font-mono text-sm mb-2 max-w-[280px]" style={{ color: T.textDim }}>
                  Recording starts automatically in {readySecs} second{readySecs === 1 ? "" : "s"}. Take a breath and think about your answer.
                </div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-2" style={{ color: T.textDim }}>
                  You will have 60 seconds. Recording stops automatically.
                </div>
              </div>
            )}

            {phase === "recording" && (
              <div className="flex flex-col items-center justify-center flex-1" data-testid={`q${Q.n}-recording`}>
                {/* Big mic + countdown */}
                <div className="relative w-28 h-28 rounded-full flex items-center justify-center mb-4"
                  style={{ background: T.danger, boxShadow: `0 0 ${30 + vol * 80}px ${T.danger}` }}>
                  <Mic className="w-12 h-12 text-white" />
                  <span className="absolute inset-0 rounded-full border-2 border-white/40" style={{ animation: "ttyPulseRing 1.4s infinite" }} />
                </div>
                <div className="font-black text-6xl leading-none mb-1" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.text }}>
                  {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
                </div>
                <div className="font-mono text-[10px] tracking-[0.35em] uppercase mb-5" style={{ color: T.danger }}>RECORDING</div>

                {/* Audio level bars */}
                <div className="flex items-center justify-center gap-1 h-12 mb-5">
                  {Array.from({ length: 24 }).map((_, i) => {
                    const seed = ((Math.sin(i * 0.6) + 1) / 2);
                    const level = vol * (0.55 + seed * 0.45);
                    const h = Math.max(6, level * 44);
                    return <div key={i} className="rounded-sm" style={{ width: 5, height: h, background: T.primary, transition: "height 60ms linear" }} />;
                  })}
                </div>

                <button onClick={stopRecording} data-testid={`q${Q.n}-stop`}
                  className="px-5 py-2 font-mono text-xs tracking-[0.25em] uppercase rounded-sm"
                  style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.text }}>
                  <Pause className="w-3.5 h-3.5 inline mr-1" /> Stop early
                </button>
              </div>
            )}

            {phase === "grading" && (
              <div className="flex flex-col items-center justify-center flex-1 text-center" data-testid={`q${Q.n}-grading`}>
                <Loader2 className="w-10 h-10 animate-spin mb-3" style={{ color: T.primary }} />
                <div className="font-mono text-sm tracking-[0.25em] uppercase mb-1" style={{ color: T.primary }}>
                  TRANSCRIBING · GRADING
                </div>
                <div className="font-mono text-xs" style={{ color: T.textDim }}>4 IELTS criteria · this takes a few seconds.</div>
              </div>
            )}

            {phase === "mic-denied" && (
              <div className="flex flex-col items-center justify-center flex-1 text-center" data-testid={`q${Q.n}-mic-denied`}>
                <AlertCircle className="w-10 h-10 mb-3" style={{ color: T.danger }} />
                <div className="font-mono text-sm" style={{ color: T.danger }}>Microphone access denied.</div>
                <div className="font-mono text-xs mt-1" style={{ color: T.textDim }}>Enable mic in browser settings and refresh.</div>
              </div>
            )}

            {phase === "done" && finalResult && (
              <div className="flex flex-col items-center justify-center flex-1 text-center" data-testid="result-overall-panel">
                <div className="font-mono text-xs tracking-[0.3em] uppercase opacity-80" style={{ color: T.textDim }}>
                  PREDICTED BAND
                </div>
                <div className="rounded-sm px-7 py-5 mt-3" style={{ background: T.primary, color: "#000", boxShadow: `0 0 30px ${T.primaryGlow}` }}>
                  <div className="font-black leading-none" data-testid="band-overall"
                    style={{ fontSize: 100, fontFamily: "'Rajdhani', sans-serif" }}>
                    {Number(finalResult.overall_band).toFixed(1)}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-2">
                    {(ctx.target_exam || "IELTS")}-STYLE
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 rounded-sm"
                  style={{ background: "rgba(22,163,74,0.12)", color: T.success, border: "1px solid rgba(22,163,74,0.3)" }}>
                  <Check className="w-3 h-3" /> HONEST · NO INFLATION
                </div>

                <button onClick={() => nav("/pricing")} data-testid="result-subscribe"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-4 font-mono text-sm tracking-[0.25em] uppercase rounded-sm transition-all hover:-translate-y-0.5"
                  style={{ background: T.primary, color: "#000", boxShadow: `0 0 18px ${T.primaryGlow}`, animation: "ttyGlow 2.5s infinite" }}>
                  <Sparkles className="w-4 h-4" /> SUBSCRIBE · LIFT MY BAND
                </button>
                <button onClick={() => nav("/app")} data-testid="result-dashboard"
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 px-5 py-3 font-mono text-xs tracking-[0.25em] uppercase rounded-sm"
                  style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.text }}>
                  <Lock className="w-3.5 h-3.5" /> GO TO MY DASHBOARD
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer progress */}
      <div className="px-6 py-3 border-t flex items-center gap-3" style={{ borderColor: T.border, background: T.panel }}>
        {personalisedQuestions.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full"
            style={{ background: i < qIdx ? T.success : i === qIdx ? T.primary : T.border, boxShadow: i === qIdx ? `0 0 10px ${T.primaryGlow}` : "none" }} />
        ))}
      </div>
    </div>
  );
}

// =================================================================
// Stage 6 — RESULT
// =================================================================
function ResultStage({ ctx, onSubscribe, onDashboard }) {
  const r = ctx.result;
  const items = r.items || [];
  const overall = r.overall_band;
  const crit = r.criteria || {};
  const terminated = !!ctx.terminated || (ctx.status && ctx.status !== "COMPLETED");

  // Auto-play Sandra's voice summary once on mount (terminated or completed).
  useEffect(() => {
    let cancelled = false;
    let audio = null;
    (async () => {
      try {
        const text = r?.voice_summary;
        if (!text) return;
        const res = await axios.post(`${API}/assessment/speak`, { text });
        if (cancelled) return;
        const b64 = res.data?.audio_b64;
        if (!b64) return;
        audio = new Audio(`data:audio/mp3;base64,${b64}`);
        await audio.play().catch(() => {});
      } catch (_e) { /* silent — non-critical */ }
    })();
    return () => {
      cancelled = true;
      try { if (audio) { audio.pause(); audio.src = ""; } } catch (_e) {}
    };
    // eslint-disable-next-line
  }, []);
  return (
    <div className="mt-3" data-testid={terminated ? "assessment-result-terminated" : "assessment-result"}>
      {terminated && (
        <div className="rounded-sm px-5 py-4 mb-5 flex items-start gap-3"
          style={{ background: "rgba(239,68,68,0.08)", border: `1px solid ${T.danger}`, boxShadow: `0 0 18px rgba(239,68,68,0.25)` }}
          data-testid="terminated-banner">
          <AlertCircle className="w-6 h-6 mt-0.5 flex-shrink-0" style={{ color: T.danger }} />
          <div>
            <div className="font-black text-xl leading-tight" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.danger }}>
              ASSESSMENT TERMINATED
            </div>
            <div className="font-mono text-sm mt-1.5 leading-6" style={{ color: T.text }}>
              Your previous attempt ended before all questions were answered. Free assessments are limited to <b>one attempt per candidate</b> — to take a new assessment, please subscribe to a prep plan.
            </div>
          </div>
        </div>
      )}

      <div className="font-mono text-xs tracking-[0.3em] uppercase" style={{ color: T.primary }}>
        // FINAL REPORT · STRICTLY VETTED · NOT INFLATED
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-5 mt-3">
        <div className="rounded-sm p-6" style={{ background: "rgba(247,127,0,0.04)", border: `1px solid ${T.border}` }}>
          <div className="font-mono text-xs tracking-[0.3em] uppercase" style={{ color: T.textDim }}>BAND BREAKDOWN</div>
          {[
            { k: "fluency",       l: "Fluency & Coherence" },
            { k: "lexical",       l: "Lexical Resource" },
            { k: "grammar",       l: "Grammatical Range" },
            { k: "pronunciation", l: "Pronunciation" },
          ].map((row) => (
            <div key={row.k} className="flex items-center justify-between mt-3" data-testid={`band-${row.k}`}>
              <div className="font-mono text-base" style={{ color: T.text }}>{row.l}</div>
              <div className="font-black text-3xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.primary, textShadow: `0 0 14px ${T.primaryGlow}` }}>
                {Number(crit[row.k] || 0).toFixed(1)}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-sm p-6 flex flex-col items-center justify-center text-center"
          style={{ background: T.primary, color: "#000", boxShadow: `0 0 24px ${T.primaryGlow}` }}
          data-testid="band-overall">
          <div className="font-mono text-xs tracking-[0.3em] uppercase opacity-80">PREDICTED BAND</div>
          <div className="font-black leading-none mt-2" style={{ fontSize: 110, fontFamily: "'Rajdhani', sans-serif" }}>
            {Number(overall).toFixed(1)}
          </div>
          <div className="font-mono text-xs tracking-[0.25em] uppercase mt-2">{(ctx.target_exam || "IELTS")}-style</div>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="band-transcripts">
          {items.map((it) => (
            <div key={it.q_index} className="rounded-sm p-4" style={{ background: "rgba(247,127,0,0.04)", border: `1px solid ${T.border}` }}>
              <div className="flex items-center justify-between">
                <div className="font-mono text-xs tracking-[0.25em] uppercase" style={{ color: T.textDim }}>Q{it.q_index}</div>
                <div className="font-black text-xl" style={{ fontFamily: "'Rajdhani', sans-serif", color: T.primary }}>
                  {Number(it.band).toFixed(1)}
                </div>
              </div>
              <div className="font-mono text-sm mt-2 line-clamp-3" style={{ color: T.text }}>{(it.transcript || "(no transcript)")}</div>
            </div>
          ))}
        </div>
      )}

      {r.voice_summary && (
        <div className="mt-6 rounded-sm p-5" style={{ background: "rgba(247,127,0,0.08)", border: `1.5px solid ${T.primary}`, boxShadow: `0 0 22px ${T.primaryGlow}` }}
             data-testid="sandra-voice-pitch">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: T.primary, color: "#000" }}>
              <Volume2 className="w-4 h-4" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.primary }}>
              SANDRA'S MESSAGE FOR YOU
            </div>
          </div>
          <div className="font-mono text-base leading-7" style={{ color: T.text }} data-testid="voice-summary-text">
            "{r.voice_summary}"
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={onSubscribe} data-testid="result-subscribe"
          className="inline-flex items-center gap-2 px-7 py-4 font-mono text-base tracking-[0.25em] uppercase rounded-sm transition-all hover:-translate-y-0.5"
          style={{ background: T.primary, color: "#000", boxShadow: `0 0 24px ${T.primaryGlow}`, animation: "ttyGlow 2.5s infinite" }}>
          <Sparkles className="w-5 h-5" /> SUBSCRIBE TO PREP · LIFT MY BAND <ChevronRight className="w-5 h-5" />
        </button>
        <button onClick={onDashboard} data-testid="result-dashboard"
          className="inline-flex items-center gap-2 px-6 py-4 font-mono text-sm tracking-[0.25em] uppercase rounded-sm"
          style={{ background: "transparent", color: T.text, border: `1px solid ${T.border}` }}>
          <Lock className="w-4 h-4" /> GO TO MY DASHBOARD
        </button>
      </div>

      <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs tracking-[0.3em] uppercase px-3 py-2 rounded-sm"
        style={{ background: "rgba(22,163,74,0.1)", color: T.success, border: "1px solid rgba(22,163,74,0.3)" }}>
        <Check className="w-3.5 h-3.5" /> HONEST ASSESSMENT · NO INFLATED SCORES
      </div>
    </div>
  );
}
